import {Component, computed, linkedSignal, Signal} from '@angular/core';
import {CommonModule} from '@angular/common';

import {CubesManagerModule} from '@studio2wx/cubes-manager';

import {
  DataChunkTitlePipe,
  DatapointMatchesPipe,
  DataTableComponent,
  DirectionIndicatorComponent,
  DrilldownComponent,
  FeaturedValueComponent,
  GetDataPointPipe,
  HighlightSentimentPipe,
  L12mBarChartComponent,
  ReportButtonComponent,
  TableLayout,
  TileHeaderComponent,
} from '@selpia/data-batch/ui';

import {TileLoaderComponent} from '@selpia/shared/ui';
import {DataChunk, DataPointParams, Measure, TileDisplay} from '@selpia/shared/models';
import {animate, style, transition, trigger} from '@angular/animations';
import {TilePreviewComponent} from '../tile-preview';

@Component({
  selector: 'lib-feature-tile-grid',
  imports: [
    CommonModule,
    CubesManagerModule,
    GetDataPointPipe,
    TileHeaderComponent,
    DrilldownComponent,
    FeaturedValueComponent,
    DataChunkTitlePipe,
    DirectionIndicatorComponent,
    TileLoaderComponent,
    L12mBarChartComponent,
    DataTableComponent,
    HighlightSentimentPipe,
    ReportButtonComponent,
    DatapointMatchesPipe,
  ],
  templateUrl: './feature-tile-grid.component.html',
  styleUrl: './feature-tile-grid.component.scss',
  animations: [
    trigger('fadeOut', [
      transition(':leave', [
        animate(
          '600ms cubic-bezier(0.4, 0.0, 1, 1)',
          style({opacity: 0}),
        ),
      ]),
    ]),
  ],
})
export class FeatureTileGridComponent extends TilePreviewComponent {
  readonly #DEFAULT_SCELETONS_COUNT = 6;

  protected readonly growthMeasure: Signal<Measure | undefined> = this.optionsFacade.impactMeasure;

  protected readonly growthMeasureLabel: Signal<string | undefined> = this.appConfigFacade.growthMeasureLabel;

  protected readonly growthParams: Signal<DataPointParams> = computed(() => {
    return {
      measureCode: this.growthMeasure()?.code,
      periodCode: this.focusPeriod(),
      date: this.focusDate(),
    };
  });

  protected readonly tileDisplay = this.optionsFacade.tileDisplay;

  protected readonly tableLayout: Signal<TableLayout | undefined> = computed(() => {
    const tileTable = this.appConfigFacade.tileTable();
    const periodCode = this.focusPeriod();

    if (!tileTable || !periodCode) {
      return;
    }

    return {
      tileTable,
      periodCode,
      date: this.focusDate(),
    };
  });

  protected measureCode = computed(() => this.growthMeasure()?.code);
  protected readonly valueRange = computed(() => {
      const dataChunks = this.filteredDataChunks();
      if (dataChunks) {
        return this.getMinMax(this.measureCode(), this.focusPeriod(), dataChunks);
      }
      return;
    },
  );

  protected readonly chunkCount = linkedSignal<DataChunk[] | undefined, number>({
    source: this.filteredDataChunks,
    computation: (newChunks, previous) => {
      return newChunks?.length || previous?.value || this.#DEFAULT_SCELETONS_COUNT;
    },
  });

  protected readonly TileDisplay = TileDisplay;
}
